/**
 * A Mermaid diagram from a ```mermaid fence, drawn where the Markdown component renders it.
 * Mermaid loads on first use as its own chunk (nothing is paid until a page shows a diagram),
 * draws at the strict security level with the theme built from the tokens (lib/diagram.ts), and
 * draws again when the theme changes. The SVG it returns is parsed and put in the page as
 * elements, never as HTML: scripts, embedded documents, event handlers and outward links are
 * removed first, and its CSS is scrubbed of imports and url() values.
 *
 * Motion (docs/design-system/language/content.md, "Diagrams in text"): the parts draw in one
 * after another in reading order on the motion tokens (a third of --duration-fast apart), so a flow is read the way it runs; an
 * edge the source marks `e1@{ animate: true }` (a data flow) keeps moving in the accent, and a
 * Pause control stops it. Reduced motion stops all of it.
 *
 * States: drawing (a skeleton), drawn, failed (what failed, why, and the source as code so it
 * can be fixed). The source is always one press away: Show source and Copy.
 */
import { Code, Pause, Play, Workflow } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '../lib/cn.ts';
import { diagramKind, diagramTheme, safeDiagramHref, scrubDiagramCss } from '../lib/diagram.ts';
import type { UiError } from '../lib/errors.ts';
import { CopyButton } from './CopyButton.tsx';
import { ErrorState } from './ErrorState.tsx';
import { IconButton } from './IconButton.tsx';
import { Skeleton } from './Skeleton.tsx';

export interface DiagramProps {
  /** Mermaid source, the body of the fence. */
  source: string;
  className?: string;
}

type Mermaid = typeof import('mermaid').default;
let lib: Promise<Mermaid> | null = null;
/** One load of mermaid for the page, on the first diagram. */
function loadMermaid(): Promise<Mermaid> {
  lib ??= import('mermaid').then((m) => m.default);
  return lib;
}
let seq = 0;

/**
 * A token's colour as an opaque hex over the panel, which is what mermaid's colour library
 * reads: the tokens hold color-mix() and other CSS colours it does not (a 1 px canvas does the
 * mixing). Font tokens and anything already hex pass through.
 */
function tokenReader(style: CSSStyleDeclaration): (token: string) => string {
  const raw = (t: string) => style.getPropertyValue(`--${t}`).trim();
  const over = raw('surface');
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  const g = canvas.getContext('2d', { willReadFrequently: true });
  return (t) => {
    const v = raw(t);
    if (t.startsWith('font') || /^#[0-9a-f]{6}$/i.test(v) || !g) return v;
    g.fillStyle = over;
    g.fillRect(0, 0, 1, 1);
    g.fillStyle = v;
    g.fillRect(0, 0, 1, 1);
    const d = g.getImageData(0, 0, 1, 1).data;
    return `#${[d[0]!, d[1]!, d[2]!].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
  };
}

/** The parts of a drawing, in the order they are read: boxes, then the lines between them and their labels. */
const PARTS = [
  '.node',
  '.cluster',
  '.actor',
  '.actor-line',
  '.note',
  '.noteText',
  '.entityBox',
  '.entityLabel',
  '.attributeBoxOdd',
  '.attributeBoxEven',
  '.stateGroup',
  '.edgePath',
  '.flowchart-link',
  '.edgeLabel',
  '.messageLine0',
  '.messageLine1',
  '.messageText',
  '.loopLine',
  '.loopText',
  '.transition',
  '.relation',
  '.relationshipLine',
  '.relationshipLabel',
].join(', ');
/** Parts past this one share the last delay, so a large drawing is not slow to read. */
const STAGGER_CAP = 24;
/** Mermaid's own classes for an edge the source animates. */
const MOVING = '.edge-animation-fast, .edge-animation-slow';

/**
 * The motion, as a style inside the SVG, scoped to its id: every part fades and rises in on the
 * motion tokens, each a step after the last; a moving edge takes the accent; reduced motion
 * stops both; a paused frame holds the moving edges still.
 */
function motionCss(id: string): string {
  const s = `#${id}`;
  return [
    `@keyframes orgus-diagram-in { from { opacity: 0; translate: 0 4px; } }`,
    `${s} .orgus-part { animation: orgus-diagram-in var(--duration-slow) var(--easing-standard) both; animation-delay: calc(var(--orgus-i, 0) * var(--duration-fast) / 3); }`,
    `${s} :is(${MOVING}) { stroke: var(--accent) !important; }`,
    `[data-paused] ${s} :is(${MOVING}) { animation-play-state: paused !important; }`,
    `@media (prefers-reduced-motion: reduce) { ${s} .orgus-part { animation: none; } ${s} :is(${MOVING}) { animation: none !important; stroke-dasharray: none !important; } }`,
  ].join('\n');
}

/**
 * The SVG mermaid drew, as elements of this document with anything that acts or reaches out
 * removed and the motion added, scoped to the id mermaid scoped its own CSS to. Returns the
 * element and whether any edge keeps moving.
 */
function safeSvg(svg: string, id: string): { node: SVGSVGElement; moving: boolean } | null {
  const doc = new DOMParser().parseFromString(svg, 'text/html');
  const root = doc.body.querySelector('svg');
  if (!root) return null;
  for (const el of root.querySelectorAll('script, iframe, object, embed')) el.remove();
  for (const el of root.querySelectorAll('*')) {
    for (const a of Array.from(el.attributes)) {
      const name = a.name.toLowerCase();
      if (name.startsWith('on')) el.removeAttribute(a.name);
      else if ((name === 'href' || name === 'xlink:href') && !safeDiagramHref(a.value)) el.removeAttribute(a.name);
    }
  }
  for (const el of root.querySelectorAll('style')) el.textContent = scrubDiagramCss(el.textContent ?? '');
  root.removeAttribute('style');
  let i = 0;
  for (const el of root.querySelectorAll(PARTS)) {
    el.classList.add('orgus-part');
    (el as HTMLElement).style.setProperty('--orgus-i', String(Math.min(i++, STAGGER_CAP)));
  }
  const style = doc.createElement('style');
  style.textContent = motionCss(id);
  root.prepend(style);
  return { node: document.importNode(root, true), moving: root.querySelector(MOVING) != null };
}

type State = { status: 'drawing' } | { status: 'drawn'; moving: boolean } | { status: 'failed'; error: UiError };

export function Diagram({ source, className }: DiagramProps) {
  const host = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<State>({ status: 'drawing' });
  const [showSource, setShowSource] = useState(false);
  const [paused, setPaused] = useState(false);
  const [themeTick, setThemeTick] = useState(0);
  const kind = useMemo(() => diagramKind(source), [source]);

  // Draw again when the theme changes: the data-theme attribute, or the computer's scheme.
  useEffect(() => {
    if (typeof MutationObserver === 'undefined') return;
    const bump = () => setThemeTick((t) => t + 1);
    const obs = new MutationObserver(bump);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class', 'style'] });
    const mq = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: light)') : null;
    mq?.addEventListener('change', bump);
    return () => {
      obs.disconnect();
      mq?.removeEventListener('change', bump);
    };
  }, []);

  useEffect(() => {
    const el = host.current;
    if (!el || !source.trim()) return;
    let live = true;
    setState({ status: 'drawing' });
    (async () => {
      try {
        const mermaid = await loadMermaid();
        if (!live) return;
        const theme = diagramTheme(tokenReader(getComputedStyle(el)));
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          suppressErrorRendering: true,
          theme: 'base',
          themeVariables: theme.variables,
          fontFamily: theme.fontFamily,
          sequence: { useMaxWidth: true },
        });
        const id = `orgus-diagram-${++seq}`;
        const { svg } = await mermaid.render(id, source);
        if (!live) return;
        const drawn = safeSvg(svg, id);
        if (!drawn) throw new Error('The drawing came back empty.');
        el.replaceChildren(drawn.node);
        setState({ status: 'drawn', moving: drawn.moving });
      } catch (e) {
        if (!live) return;
        el.replaceChildren();
        const why = (e instanceof Error ? e.message : String(e)).split('\n').find((l) => l.trim()) ?? 'Mermaid could not read it.';
        setState({ status: 'failed', error: { what: 'The diagram did not draw.', why: why.trim(), next: 'Fix the Mermaid source below, or keep it as code.' } });
      }
    })();
    return () => {
      live = false;
    };
  }, [source, themeTick]);

  if (!source.trim()) return null;
  const sourceShown = showSource || state.status === 'failed';
  const moving = state.status === 'drawn' && state.moving;
  return (
    <figure className={cn('overflow-hidden rounded-card border border-border-subtle bg-surface', className)} data-diagram={kind} data-paused={paused || undefined}>
      <div className="flex h-control-sm items-center gap-2 border-b border-border-subtle pl-3 pr-1 text-caption text-fg-subtle">
        <Workflow className="size-3.5" aria-hidden />
        <span>{kind}</span>
        {state.status === 'drawing' && <span aria-live="polite">Drawing</span>}
        <span className="ml-auto flex items-center">
          {moving && <IconButton size="xs" label={paused ? 'Play the flow' : 'Pause the flow'} icon={paused ? <Play /> : <Pause />} pressed={paused} onClick={() => setPaused((p) => !p)} />}
          <IconButton size="xs" label={showSource ? 'Hide source' : 'Show source'} icon={<Code />} pressed={showSource} onClick={() => setShowSource((s) => !s)} />
          <CopyButton text={source} label="Copy source" />
        </span>
      </div>
      {state.status === 'drawing' && (
        <div role="status" aria-label="Drawing the diagram" className="p-3">
          <Skeleton className="h-40 w-full" />
        </div>
      )}
      <div ref={host} role="img" aria-label={kind} className={cn('overflow-x-auto p-3 [&>svg]:mx-auto [&>svg]:h-auto [&>svg]:max-w-full', state.status !== 'drawn' && 'hidden')} />
      {state.status === 'failed' && (
        <div className="p-2">
          <ErrorState variant="inline" error={state.error} />
        </div>
      )}
      {sourceShown && (
        <pre className="overflow-x-auto border-t border-border-subtle px-3 py-2 font-mono text-caption">
          <code>{source}</code>
        </pre>
      )}
    </figure>
  );
}
